import { useConfirmation } from '../context/ConfirmationContext';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import TopBar from '../components/layout/TopBar';
import MapCanvas from '../components/Map/MapCanvas';
import SectionsAndTypes from '../components/Map/SectionsAndTypes';
import PolygonEditor from '../components/Map/PolygonEditor';
import { polygonError } from '../components/Map/polygon';
import LotDetailDrawer from '../components/Map/LotDetailDrawer';
import { Icon } from '@iconify/react';
import {
  GlassCard, Button, Badge, InputField, Modal, SectionHeader, DataTable,
} from '../components/ui';
import { useToast } from '../context/ToastContext';
import type { Lot, LotStatus, Section, LotType, InventoryPolicy, LotAuditLog } from '../types/inventory';
import { STATUS_CONFIG, CATEGORY_CONFIG, fmt } from '../types/inventory';
import { moveSectionLots, lotBounds } from '../components/Map/geometry';
import './Inventory.css';
import { api } from '../lib/api';

type ViewMode = 'map' | 'list' | 'designer' | 'policies';

const Inventory: React.FC = () => {
  const { addToast } = useToast();
  const { confirmAction } = useConfirmation();
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [lots, setLots] = useState<Lot[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [lotTypes, setLotTypes] = useState<LotType[]>([]);
  const [auditLogs, setAuditLogs] = useState<LotAuditLog[]>([]);
  const [policies, setPolicies] = useState<InventoryPolicy[]>([]);
  const [shapeValid, setShapeValid] = useState(true);
  const [savingSetup, setSavingSetup] = useState(false);
  const [savingFreeform, setSavingFreeform] = useState(false);
  const [showFreeformModal, setShowFreeformModal] = useState(false);
  const [freeformSection, setFreeformSection] = useState<Section | null>(null);
  const [freeformForm, setFreeformForm] = useState<any>({});
  const [placementMode, setPlacementMode] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingLayout, setSavingLayout] = useState(false);
  const [layoutDirty, setLayoutDirty] = useState(false);
  const layoutSnapshot = useRef<{ lots: Lot[]; sections: Section[] } | null>(null);
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null);
  const [filterStatus, setFilterStatus] = useState<LotStatus | ''>('');
  const [filterSection, setFilterSection] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [auditError, setAuditError] = useState('');
  const [auditVersion, setAuditVersion] = useState(0);
  const selectedLotId = selectedLot?.id;

  const fetchInventory = useCallback(async () => {
    setLoadError('');
    try {
      const [secData, ltData, lotData, policyData] = await Promise.all([
        api.inventory.sections.list(), api.inventory.lotTypes.list(), api.inventory.lots.list(), api.inventory.policies.list(),
      ]);
      setSections(secData.map((section: Section) => ({ ...section, layoutMode: section.layoutMode || 'GRID' })));
      setLotTypes(ltData);
      setLots(lotData);
      setPolicies(policyData);
      setSelectedLot(current => current ? lotData.find((lot: Lot) => lot.id === current.id) || null : null);
    } catch (error: any) {
      setLoadError(error.message || 'Inventory could not be loaded.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchInventory(); }, [fetchInventory]);
  useEffect(() => {
    if (!selectedLotId) return;
    let cancelled = false;
    setAuditLogs([]);
    setAuditError('');
    api.inventory.auditLogs.list(selectedLotId).then(logs => { if (!cancelled) setAuditLogs(logs); })
      .catch(() => { if (!cancelled) setAuditError('History could not be loaded. Reopen this lot to try again.'); });
    return () => { cancelled = true; };
  }, [selectedLotId, auditVersion]);
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setSelectedLot(null); setPlacementMode(false); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);
  useEffect(() => {
    if (!layoutDirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    const guardNavigation = (event: MouseEvent) => {
      if ((event.target as HTMLElement).closest('a[href]') && !window.confirm('Discard your unsaved layout changes and leave?')) {
        event.preventDefault(); event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', warn);
    document.addEventListener('click', guardNavigation, true);
    return () => { window.removeEventListener('beforeunload', warn); document.removeEventListener('click', guardNavigation, true); };
  }, [layoutDirty]);

  // Designer state
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionForm, setSectionForm] = useState<any>({});

  // Lot Type CRUD state
  const [editingLotType, setEditingLotType] = useState<LotType | null>(null);
  const [showLotTypeModal, setShowLotTypeModal] = useState(false);
  const [lotTypeForm, setLotTypeForm] = useState<any>({});

  // ─── Computed stats ───────────────────────────────────
  const stats = useMemo((): Record<string, number> => {
    const activeLots = lots.filter(lot => sections.some(section => section.id === lot.sectionId && section.isActive));
    const result: Record<string, number> = {};
    Object.keys(STATUS_CONFIG).forEach(s => { result[s] = activeLots.filter(l => l.status === s).length; });
    result.total = activeLots.length;
    result.totalValue = activeLots.reduce((s, l) => s + l.price, 0);
    result.soldValue = activeLots.filter(l => l.status === 'SOLD').reduce((s, l) => s + l.price, 0);
    return result;
  }, [lots, sections]);

  // ─── Filtered lots for list view ──────────────────────
  const filteredLots = useMemo(() => {
    return lots.filter(l => {
      if (filterStatus && l.status !== filterStatus) return false;
      if (filterSection && l.sectionId !== filterSection) return false;
      const section = sections.find(s => s.id === l.sectionId);
      if (!section?.isActive) return false;
      const query = searchQuery.trim().toLowerCase();
      if (query && ![l.lotNumber, l.occupantName, l.lotType?.name, section.label].some(value => value?.toLowerCase().includes(query))) return false;
      return true;
    });
  }, [lots, sections, filterStatus, filterSection, searchQuery]);
  const visibleLotIds = useMemo(() => new Set(filteredLots.map(lot => lot.id)), [filteredLots]);

  // ─── Handlers ─────────────────────────────────────────
  const updateLot = async (lotId: string, data: Partial<Lot> & { _reason?: string }) => {
    try {
      const updated = await api.inventory.lots.update(lotId, data);
      setLots(current => current.map(lot => lot.id === lotId ? updated : lot));
      setSelectedLot(current => current?.id === lotId ? updated : current);
      setAuditVersion(value => value + 1);
      return true;
    } catch (error: any) {
      addToast('error', 'Change not saved', error.message || 'Try again.');
      return false;
    }
  };
  const handleStatusChange = async (lotId: string, status: LotStatus, reason?: string) => {
    const saved = await updateLot(lotId, { status, _reason: reason });
    if (saved) addToast('success', 'Status updated');
    return saved;
  };
  const handlePriceChange = async (lotId: string, price: number) => {
    if (!Number.isFinite(price) || price < 0) return false;
    const saved = await updateLot(lotId, { price });
    if (saved) addToast('success', 'Price updated', fmt(price));
    return saved;
  };
  const handleLockToggle = async (lotId: string, isLocked: boolean, reason?: string) => {
    const saved = await updateLot(lotId, { isLocked, lockedReason: isLocked ? reason : '' });
    if (saved) addToast('success', isLocked ? 'Lot locked' : 'Lot unlocked');
    return saved;
  };
  const handleAddNote = async (lotId: string, note: string) => {
    const lot = lots.find(item => item.id === lotId);
    const saved = await updateLot(lotId, { notes: [lot?.notes, note].filter(Boolean).join('\n') });
    if (saved) addToast('success', 'Note saved');
    return saved;
  };
  const handleRepositionLot = async (lotId: string, data: { x_coord: number; y_coord: number; width?: number; height?: number }) => {
    if (Object.values(data).some(value => value !== undefined && !Number.isFinite(value)) || (data.width !== undefined && data.width < 20) || (data.height !== undefined && data.height < 20)) {
      addToast('error', 'Invalid dimensions', 'Width and height must be at least 20 map units.');
      return false;
    }
    const saved = await updateLot(lotId, data);
    if (saved) addToast('success', 'Position updated');
    return saved;
  };
  const beginLayoutEdit = () => {
    layoutSnapshot.current = { lots, sections };
    setLayoutDirty(false); setSelectedLot(null); setPlacementMode(false); setEditMode(true);
  };
  const discardLayout = () => {
    if (layoutSnapshot.current) { setLots(layoutSnapshot.current.lots); setSections(layoutSnapshot.current.sections); }
    layoutSnapshot.current = null; setLayoutDirty(false); setEditMode(false);
  };
  const handleLotDragEnd = (lotId: string, x_coord: number, y_coord: number) => {
    setLots(current => current.map(lot => lot.id === lotId ? { ...lot, x_coord, y_coord, isFreeform: true } : lot));
    setLayoutDirty(true);
  };
  const handleSectionDragEnd = (sectionId: string, x: number, y: number) => {
    const section = sections.find(item => item.id === sectionId);
    if (!section || (section.x === x && section.y === y)) return;
    setLots(current => moveSectionLots(current, section, x, y));
    setSections(current => current.map(item => item.id === sectionId ? { ...item, x, y } : item));
    setLayoutDirty(true);
  };
  const handleLotResize = (lotId: string, x_coord: number, y_coord: number, width: number, height: number) => {
    setLots(current => current.map(lot => lot.id === lotId ? { ...lot, x_coord, y_coord, width, height, isFreeform: true } : lot));
    setLayoutDirty(true);
  };
  const handleEditDone = async () => {
    const snapshot = layoutSnapshot.current;
    if (!snapshot || savingLayout) return;
    const changedSections = sections.filter(section => section !== snapshot.sections.find(item => item.id === section.id))
      .map(({ id, x, y, updatedAt }) => ({ id, x, y, expectedUpdatedAt: updatedAt }));
    const changedLots = lots.filter(lot => lot !== snapshot.lots.find(item => item.id === lot.id))
      .map(({ id, x_coord, y_coord, width, height, isFreeform, points, updatedAt }) => ({ id, x_coord, y_coord, width, height, isFreeform, points, expectedUpdatedAt: updatedAt }));
    setSavingLayout(true);
    try {
      await api.inventory.saveLayout({ sections: changedSections, lots: changedLots });
      layoutSnapshot.current = null; setLayoutDirty(false); setEditMode(false);
      addToast('success', 'Layout saved', 'All positions and dimensions have been saved together.');
      await fetchInventory();
    } catch (error: any) {
      addToast('error', 'Layout not saved', error.message || 'Your draft is still here. Try again.');
    } finally { setSavingLayout(false); }
  };

  const handleDeleteLot = async (lotId: string) => {
    const lot = lots.find(l => l.id === lotId);
    await confirmAction({ title: `Delete lot ${lot?.lotNumber || ''}?`, description: 'This permanently removes the lot from the park map and inventory. This cannot be undone.', confirmLabel: 'Delete lot', successTitle: 'Lot deleted', errorTitle: 'Lot not deleted', action: async () => {
      await api.inventory.lots.delete(lotId);
      setLots(prev => prev.filter(l => l.id !== lotId)); setSelectedLot(null);
    }});
  };

  const handlePolicyChange = async (policyId: string, newValue: string) => {
    try {
      const updated = await api.inventory.policies.update(policyId, { value: newValue });
      setPolicies(current => current.map(policy => policy.id === policyId ? updated : policy));
      addToast('success', 'Policy saved');
      return true;
    } catch (error: any) { addToast('error', 'Policy not saved', error.message); return false; }
  };

  const commitPolicyInput = async (input: HTMLInputElement, policy: InventoryPolicy) => {
    if (input.value !== policy.value && !await handlePolicyChange(policy.id, input.value)) input.value = policy.value;
  };

  // Delete section
  const handleDeleteSection = async (sec: Section) => {
    if (lots.some(lot => lot.sectionId === sec.id)) {
      addToast('error', 'Section contains lots', 'Remove eligible lots individually before deleting this section.');
      return;
    }
    await confirmAction({ title: `Delete ${sec.label}?`, description: 'This permanently removes the empty section. This cannot be undone.', confirmLabel: 'Delete section', successTitle: 'Section deleted', errorTitle: 'Section not deleted', action: async () => {
      await api.inventory.sections.delete(sec.id);
      setSections(prev => prev.filter(s => s.id !== sec.id));
    }});
  };

  // Section designer
  const openSectionEditor = (sec?: Section) => {
    if (sec) {
      setEditingSection(sec);
      setSectionForm({ ...sec });
    } else {
      setEditingSection(null);
      setSectionForm({
        name: '', label: '', description: '', initialLotTypeId: lotTypes.find(type => type.isActive && type.category === 'LAWN')?.id || '', x: sections.length ? Math.max(...sections.map(section => section.x + section.width)) + 100 : 100, y: 100, width: 400, height: 300,
        layoutMode: 'GRID', rows: 4, cols: 6, lotWidth: 48, lotHeight: 48, lotGap: 6,
        phase: '', category: 'LAWN', color: '#22C55E',
      });
    }
    setShowSectionModal(true);
  };

  const handleSaveSection = async () => {
    if (!sectionForm.name || !sectionForm.label) {
      addToast('error', 'Validation', 'Name and label are required.');
      return;
    }
    if (['x', 'y', 'width', 'height', 'lotWidth', 'lotHeight', 'lotGap', 'rows', 'cols'].some(key => !Number.isFinite(Number(sectionForm[key]))) ||
        ['width', 'height', 'lotWidth', 'lotHeight'].some(key => Number(sectionForm[key]) < 20) || Number(sectionForm.lotGap) < 0 ||
        ['rows', 'cols'].some(key => !Number.isInteger(Number(sectionForm[key])) || Number(sectionForm[key]) < 1 || Number(sectionForm[key]) > 100)) {
      addToast('error', 'Check section dimensions', 'Use dimensions of at least 20, a nonnegative gap, and 1–100 rows and columns.'); return;
    }
    if (savingSetup) return;
    if (!editingSection && sectionForm.layoutMode !== 'FREEFORM' && !sectionForm.initialLotTypeId) { addToast('error', 'Choose a lot type', 'Select the type for the generated grid lots.'); return; }
    setSavingSetup(true);
    try {
      if (editingSection) {
        const { id, _count, ...updateData } = sectionForm;
        const updated = await api.inventory.sections.update(editingSection.id, updateData);
        setSections(prev => prev.map(s => s.id === editingSection.id ? { ...s, ...updated, layoutMode: updated.layoutMode || 'GRID' } : s));
        await fetchInventory();
        addToast('success', 'Section Updated', `${sectionForm.label} has been updated.`);
      } else {
        const mode = sectionForm.layoutMode || 'GRID';
        // Auto-calculate section dimensions from grid config for consistent margins
        const PAD_X = 24, PAD_TOP = 40, PAD_BOTTOM = 16;
        const lw = sectionForm.lotWidth || 48, lh = sectionForm.lotHeight || 48, gap = sectionForm.lotGap ?? 6;
        const rows = sectionForm.rows || 4, cols = sectionForm.cols || 6;
        const calcWidth = mode !== 'FREEFORM' ? PAD_X * 2 + cols * lw + Math.max(0, cols - 1) * gap : (sectionForm.width || 400);
        const calcHeight = mode !== 'FREEFORM' ? PAD_TOP + PAD_BOTTOM + rows * lh + Math.max(0, rows - 1) * gap : (sectionForm.height || 300);
        await api.inventory.sections.create({
          ...sectionForm, rotation: 0, isActive: true, sortOrder: sections.length + 1,
          layoutMode: mode, width: calcWidth, height: calcHeight, generateLots: mode !== 'FREEFORM',
        });
        await fetchInventory();
        addToast('success', 'Section created', mode === 'FREEFORM' ? 'Use Place lot to add individual lots.' : `${sectionForm.label} with ${rows * cols} grid lots.`);
      }
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save section');
      return;
    } finally { setSavingSetup(false); }
    setShowSectionModal(false);
  };

  // ─── Lot Type CRUD ────────────────────────────────────
  const openLotTypeEditor = (lt?: LotType) => {
    if (lt) {
      setEditingLotType(lt);
      setLotTypeForm({ ...lt });
    } else {
      setEditingLotType(null);
      setLotTypeForm({
        name: '', code: '', category: 'LAWN', description: '',
        defaultWidth: 48, defaultHeight: 48, capacity: 1,
        basePrice: 50000, color: '#0D9488', icon: '', imageUrl: '', isActive: true,
      });
    }
    setShowLotTypeModal(true);
  };

  const handleSaveLotType = async () => {
    if (!lotTypeForm.name || !lotTypeForm.code) {
      addToast('error', 'Validation', 'Name and code are required.');
      return;
    }
    if (savingSetup) return;
    if (![lotTypeForm.basePrice,lotTypeForm.defaultWidth,lotTypeForm.defaultHeight,lotTypeForm.capacity].every(value => Number.isFinite(Number(value))) || Number(lotTypeForm.basePrice) < 0 || Number(lotTypeForm.defaultWidth) < 20 || Number(lotTypeForm.defaultHeight) < 20 || !Number.isInteger(Number(lotTypeForm.capacity)) || Number(lotTypeForm.capacity) < 1) { addToast('error','Invalid defaults','Use dimensions of at least 20 map units, a nonnegative price, and a whole-number capacity.'); return; }
    setSavingSetup(true);
    try {
      if (editingLotType) {
        const { id, ...updateData } = lotTypeForm;
        const updated = await api.inventory.lotTypes.update(editingLotType.id, updateData);
        setLotTypes(prev => prev.map(t => t.id === editingLotType.id ? updated : t));
        setLots(prev => prev.map(lot => lot.lotTypeId === editingLotType.id ? { ...lot, lotType: updated } : lot));
        addToast('success', 'Lot Type Updated', `${lotTypeForm.name} has been updated.`);
      } else {
        const created = await api.inventory.lotTypes.create({ ...lotTypeForm, sortOrder: lotTypes.length + 1 });
        setLotTypes(prev => [...prev, created]);
        addToast('success', 'Lot Type Created', `${lotTypeForm.name} has been added.`);
      }
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save lot type');
      return;
    } finally { setSavingSetup(false); }
    setShowLotTypeModal(false);
  };

  const handleDeleteLotType = async (lt: LotType) => {
    const inUse = lots.some(l => l.lotTypeId === lt.id);
    if (inUse) {
      addToast('error', 'Cannot Delete', `${lt.name} is in use by existing lots.`);
      return;
    }
    await confirmAction({ title: `Delete ${lt.name}?`, description: 'This permanently removes the unused lot type. This cannot be undone.', confirmLabel: 'Delete lot type', successTitle: 'Lot type deleted', errorTitle: 'Lot type not deleted', action: async () => {
      await api.inventory.lotTypes.delete(lt.id);
      setLotTypes(prev => prev.filter(t => t.id !== lt.id));
    }});
  };

  const handleToggleLotType = async (lt: LotType) => {
    try {
      await api.inventory.lotTypes.update(lt.id, { isActive: !lt.isActive });
    } catch (error: any) { addToast('error', 'Change not saved', error.message); return; }
    setLotTypes(prev => prev.map(t => t.id === lt.id ? { ...t, isActive: !t.isActive } : t));
    addToast('info', lt.isActive ? 'Lot Type Disabled' : 'Lot Type Enabled');
  };

  const nextLotNumber = (section: Section) => {
    const used = new Set(lots.map(lot => lot.lotNumber));
    let number = 1;
    while (used.has(`${section.name}${number}`)) number++;
    return `${section.name}${number}`;
  };

  // ─── Freeform Lot Handlers ─────────────────────────────
  const openFreeformModal = (sec: Section) => {
    setFreeformSection(sec);
    setFreeformForm({
      lotNumber: nextLotNumber(sec),
      points: null,
      lotTypeId: lotTypes.find(type => type.isActive && type.category === sec.category)?.id || '',
      price: lotTypes.find(type => type.isActive && type.category === sec.category)?.basePrice ?? 0,
      x_coord: 24,
      y_coord: Math.max(40, ...lots.filter(lot=>lot.sectionId===sec.id).map(lot=>{const box=lotBounds(lot,sec);return box.y+box.height-sec.y+8;})),
      width: sec.lotWidth || 60,
      height: sec.lotHeight || 60,
    });
    setShowFreeformModal(true);
  };

  const handleSaveFreeformLot = async () => {
    if (!freeformSection || !freeformForm.lotNumber) {
      addToast('error', 'Validation', 'Lot number is required.');
      return;
    }
    if (![freeformForm.price, freeformForm.x_coord, freeformForm.y_coord, freeformForm.width, freeformForm.height].every(value => value !== '' && Number.isFinite(Number(value))) || Number(freeformForm.price) < 0 || Number(freeformForm.width) < 20 || Number(freeformForm.height) < 20) {
      addToast('error', 'Check the lot details', 'Enter a valid price and position. Width and height must be at least 20 map units.'); return;
    }
    if (lots.some(lot => lot.lotNumber.toLowerCase() === freeformForm.lotNumber.trim().toLowerCase())) {
      addToast('error', 'Lot number already exists', 'Choose a unique lot number.'); return;
    }
    if (savingFreeform) return;
    if (!shapeValid || (freeformForm.points && polygonError(freeformForm.points))) { addToast('error', 'Complete the outline', 'Close the polygon and fix any crossing edges before saving.'); return; }
    setSavingFreeform(true);
    try {
      const created = await api.inventory.lots.create({
        sectionId: freeformSection.id,
        points: freeformForm.points || null,
        lotTypeId: freeformForm.lotTypeId || undefined,
        lotNumber: freeformForm.lotNumber.trim(),
        price: Number(freeformForm.price),
        status: 'AVAILABLE',
        row: 0, col: 0,
        isFreeform: true,
        x_coord: freeformSection.x + Number(freeformForm.x_coord),
        y_coord: freeformSection.y + Number(freeformForm.y_coord),
        width: Number(freeformForm.width),
        height: Number(freeformForm.height),
      });
      setLots(prev => [...prev, { ...created, isFreeform: true }]);
      addToast('success', 'Lot Created', `Freeform lot ${freeformForm.lotNumber} added to ${freeformSection.label}`);
      setShowFreeformModal(false);
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to create lot');
    } finally { setSavingFreeform(false); }
  };

  // Click-to-place on canvas
  const handlePlaceLot = (sectionId: string, absX: number, absY: number) => {
    const sec = sections.find(s => s.id === sectionId);
    if (!sec) return;
    // Open freeform modal pre-filled with the click coordinates (relative to section)
    const relX = Math.max(0, absX - sec.x);
    const relY = Math.max(0, absY - sec.y);
    setFreeformSection(sec);
    setFreeformForm({
      lotNumber: nextLotNumber(sec),
      points: null,
      lotTypeId: lotTypes.find(type => type.isActive && type.category === sec.category)?.id || '',
      price: lotTypes.find(type => type.isActive && type.category === sec.category)?.basePrice ?? 0,
      x_coord: Math.round(relX),
      y_coord: Math.round(relY),
      width: sec.lotWidth || 60,
      height: sec.lotHeight || 60,
    });
    setShowFreeformModal(true);
    setPlacementMode(false);
  };

  // ─── View mode tabs config ────────────────────────────
  const viewTabs: { key: ViewMode; label: string; icon: string }[] = [
    { key: 'map', label: 'Map', icon: 'solar:map-bold-duotone' },
    { key: 'list', label: 'List', icon: 'solar:list-bold-duotone' },
    { key: 'designer', label: 'Sections & types', icon: 'solar:ruler-pen-bold-duotone' },
    { key: 'policies', label: 'Policies', icon: 'solar:shield-bold-duotone' },
  ];

  // ─── Status badge variant ────────────────────────────
  const statusVariant = (s: string) => {
    switch (s) {
      case 'AVAILABLE': return 'available';
      case 'RESERVED': return 'reserved';
      case 'SOLD': return 'sold';
      default: return 'default';
    }
  };

  // ─── List columns ─────────────────────────────────────
  const listColumns = [
    {
      key: 'lotNumber', header: 'Lot',
      render: (row: Lot) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: STATUS_CONFIG[row.status]?.color || '#64748B',
          }} />
          <button className="inventory-lot-link" onClick={event => { event.stopPropagation(); setSelectedLot(row); }}>{row.lotNumber}</button>
          {row.isFreeform && <Badge variant="reserved" style={{ fontSize: '9px', padding: '1px 5px' }}>{row.points?.length ? 'Polygon' : 'Custom'}</Badge>}
        </div>
      ),
    },
    {
      key: 'section', header: 'Section',
      render: (row: Lot) => {
        const sec = sections.find(s => s.id === row.sectionId);
        return <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{sec?.label || row.sectionId}</span>;
      },
    },
    {
      key: 'type', header: 'Type',
      render: (row: Lot) => <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{row.lotType?.name || '—'}</span>,
    },
    {
      key: 'price', header: 'Price',
      render: (row: Lot) => <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{fmt(row.price)}</span>,
    },
    {
      key: 'status', header: 'Status',
      render: (row: Lot) => <Badge variant={statusVariant(row.status) as any}>{row.status}</Badge>,
    },
    {
      key: 'occupant', header: 'Occupant',
      render: (row: Lot) => <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{row.occupantName || '—'}</span>,
    },
  ];

  // ─── RENDER: Status Bar ───────────────────────────────
  const renderStatusBar = () => (
    <div className="inventory-status-filters" role="group" aria-label="Filter by lot status">
      <button type="button" aria-pressed={!filterStatus} onClick={() => setFilterStatus('')}>All statuses <strong>{stats.total}</strong></button>
      {(Object.entries(STATUS_CONFIG) as [LotStatus, typeof STATUS_CONFIG[LotStatus]][]).map(([key, config]) => (
        <button type="button" key={key} aria-pressed={filterStatus === key} onClick={() => setFilterStatus(filterStatus === key ? '' : key)}>
          <span className="inventory-status-dot" style={{ background: config.color }} />{config.label}<strong>{stats[key] || 0}</strong>
        </button>
      ))}
    </div>
  );
  const hasFreeformSections = sections.some(section => section.isActive && section.layoutMode !== 'GRID');
  const renderDrawer = () => selectedLot && (
      <LotDetailDrawer key={selectedLot.id} lot={selectedLot} section={sections.find(section => section.id === selectedLot.sectionId)}
        auditLogs={auditLogs} auditError={auditError} onClose={() => setSelectedLot(null)} onStatusChange={handleStatusChange}
        onPriceChange={handlePriceChange} onLockToggle={handleLockToggle} onAddNote={handleAddNote}
        onDelete={handleDeleteLot} onReposition={handleRepositionLot}
        onShapeChange={(id, points) => { const lot=lots.find(item=>item.id===id)!; const section=sections.find(item=>item.id===lot.sectionId)!; const bounds=lotBounds(lot,section); return updateLot(id, { points, isFreeform: true, x_coord: bounds.x, y_coord: bounds.y }); }}
        onPropertySaved={() => { fetchInventory(); setAuditVersion(v => v + 1); }}
        onTransactionComplete={() => { fetchInventory(); setSelectedLot(null); }} />
  );
  const renderMapView = () => <>
    <div className={`inventory-map-guidance${editMode ? ' is-editing' : ''}`}>
      <div>
        <strong>{editMode ? (layoutDirty ? 'Unsaved layout changes' : 'Editing layout') : placementMode ? 'Choose a lot position' : 'Explore the park'}</strong>
        <span>{editMode ? 'Drag sections to move them. In hybrid or freeform sections, select a lot to move or resize it.'
          : placementMode ? 'Click or tap inside a freeform or hybrid section. Press Escape to cancel.'
          : 'Drag to pan · Scroll to zoom · Choose a section to focus. Select a lot for details, or browse the List.'}</span>
      </div>
      {editMode && <div className="inventory-actions">
        <Button variant="secondary" size="sm" onClick={discardLayout} disabled={savingLayout}>{layoutDirty ? 'Discard' : 'Exit editing'}</Button>
        <Button variant="primary" size="sm" onClick={handleEditDone} loading={savingLayout} disabled={!layoutDirty}>Save layout</Button>
      </div>}
      {placementMode && <Button variant="secondary" size="sm" onClick={() => setPlacementMode(false)}>Cancel placement</Button>}
    </div>
    <div className="inventory-workspace">
      <div className="inventory-map-surface">
        <MapCanvas sections={sections} lots={lots} selectedLotId={selectedLot?.id} onLotClick={setSelectedLot}
          onSectionDragEnd={handleSectionDragEnd} visibleLotIds={visibleLotIds} filterSection={filterSection}
          placementMode={placementMode} onPlaceLot={handlePlaceLot} editMode={editMode} disabled={savingLayout}
          onLotDragEnd={handleLotDragEnd} onLotResize={handleLotResize} />
        {filteredLots.length === 0 && <div className="inventory-map-empty" role="status">
          <strong>{lots.length ? 'No matching lots' : 'No lots yet'}</strong>
          <span>{lots.length ? 'Clear the filters or search for another lot.' : 'Create a section in Sections & types to start building the map.'}</span>
        </div>}
      </div>
    </div>
  </>;
  const renderListView = () => <div className="inventory-list-layout">
    <div className="inventory-table-scroll">
      {filteredLots.length ? <DataTable columns={listColumns} data={filteredLots} onRowClick={(lot: Lot) => setSelectedLot(lot)} />
        : <div className="inventory-state"><strong>No matching lots</strong><p>Try a different lot number, section, or status.</p></div>}
    </div>
  </div>;

  // ─── RENDER: Section Designer ─────────────────────────
  const renderDesigner = () => <SectionsAndTypes sections={sections} lots={lots} lotTypes={lotTypes}
    onEditSection={openSectionEditor} onDeleteSection={handleDeleteSection} onAddLot={openFreeformModal}
    onViewMap={(section, edit) => { setSearchQuery(''); setFilterStatus(''); setFilterSection(section.id); setViewMode('map'); if(edit) beginLayoutEdit(); }}
    onToggleSection={async section => { try { await api.inventory.sections.update(section.id,{isActive:!section.isActive}); await fetchInventory(); } catch(error:any) { addToast('error','Section not updated',error.message); } }}
    onEditType={openLotTypeEditor} onDeleteType={handleDeleteLotType} onToggleType={handleToggleLotType} />;

  // ─── RENDER: Policies ─────────────────────────────────
  const policyCategories = ['reservation', 'pricing', 'transfer', 'approval', 'status', 'maintenance'];
  const policyCategoryConfig: Record<string, { label: string; icon: string; description: string }> = {
    reservation: { label: 'Reservation Rules', icon: 'solar:clock-circle-bold-duotone', description: 'Control how lots are reserved and when reservations expire' },
    pricing: { label: 'Pricing Policies', icon: 'solar:tag-price-bold-duotone', description: 'Manage discounts, seasonal pricing, and markups' },
    transfer: { label: 'Transfer & Upgrade', icon: 'solar:transfer-horizontal-bold-duotone', description: 'Control lot ownership transfers and type upgrades' },
    approval: { label: 'Approval Workflows', icon: 'solar:shield-check-bold-duotone', description: 'Require manager approval for sensitive operations' },
    status: { label: 'Status Workflow', icon: 'solar:routing-bold-duotone', description: 'Define allowed status transitions and queuing' },
    maintenance: { label: 'Maintenance & Limits', icon: 'solar:settings-bold-duotone', description: 'Maintenance holds, blackout dates, agent limits' },
  };

  const renderPolicies = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <SectionHeader title="Inventory Policies" icon="solar:shield-bold-duotone" />
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: '-12px' }}>
        Saved policy settings for this inventory. Enforcement depends on the related workflow; these settings do not automatically create approval or scheduling rules.
      </div>

      {policyCategories.map(cat => {
        const catPolicies = policies.filter(p => p.category === cat);
        const cfg = policyCategoryConfig[cat];
        if (catPolicies.length === 0) return null;
        return (
          <GlassCard key={cat}>
            <SectionHeader title={cfg.label} icon={cfg.icon} />
            <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '12px', marginTop: '-4px' }}>{cfg.description}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {catPolicies.map(policy => (
                <div key={policy.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', borderRadius: '6px', background: 'var(--bg-elevated)',
                }}>
                  <div style={{ flex: 1, marginRight: '12px' }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 500 }}>{policy.label}</div>
                    {policy.description && <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '1px' }}>{policy.description}</div>}
                  </div>
                  {policy.valueType === 'boolean' ? (
                    <button role="switch" aria-checked={policy.value === 'true'} aria-label={policy.label} onClick={() => handlePolicyChange(policy.id, policy.value === 'true' ? 'false' : 'true')}
                      style={{
                        width: '44px', height: '24px', borderRadius: '12px', position: 'relative',
                        border: 'none', cursor: 'pointer', flexShrink: 0,
                        background: policy.value === 'true' ? 'var(--primary-600)' : 'var(--dark-700)',
                        transition: 'background 0.2s',
                      }}>
                      <span style={{
                        width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '3px', transition: 'left 0.2s',
                        left: policy.value === 'true' ? '23px' : '3px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                  ) : policy.valueType === 'number' ? (
                    <input
                      type="number" key={`${policy.id}-${policy.value}`} defaultValue={policy.value} aria-label={policy.label}
                      onBlur={e => commitPolicyInput(e.currentTarget, policy)}
                      style={{
                        width: '80px', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '6px 10px', color: 'var(--input-text)', fontSize: '13px',
                        fontFamily: 'var(--font-body)', outline: 'none', textAlign: 'right',
                      }}
                    />
                  ) : policy.valueType === 'json' ? (
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontFamily: 'var(--font-code)' }}>
                      JSON Config
                    </span>
                  ) : (
                    <input
                      key={`${policy.id}-${policy.value}`} defaultValue={policy.value} aria-label={policy.label}
                      onBlur={e => commitPolicyInput(e.currentTarget, policy)}
                      style={{
                        width: '140px', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '6px 10px', color: 'var(--input-text)', fontSize: '13px',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </GlassCard>
        );
      })}
    </div>
  );

  // ─── Main Render ──────────────────────────────────────
  return (
    <>
      <TopBar title="Map Inventory" showSearch={false} />
      <div className="inventory-page">
        <div className="inventory-heading">
          <div><h2>Park inventory</h2><p>Find available lots, review records, and manage the park layout.</p></div>
          {!loading && !loadError && <div className="inventory-summary"><span><strong>{stats.total}</strong> lots</span><span><strong>{sections.filter(section => section.isActive).length}</strong> active sections</span><span>Listed value <strong>{fmt(stats.totalValue)}</strong></span></div>}
        </div>
        <div className="inventory-navigation">
          <div className="inventory-view-tabs" role="group" aria-label="Inventory view">
            {viewTabs.map(tab => <button type="button" key={tab.key} aria-pressed={viewMode === tab.key} disabled={editMode}
              onClick={() => { setViewMode(tab.key); setSelectedLot(null); setPlacementMode(false); }}>
              <Icon icon={tab.icon} width={16} />{tab.label}
            </button>)}
          </div>
          {viewMode === 'map' && !editMode && !loading && !loadError && <div className="inventory-actions">
            <Button variant="secondary" size="sm" icon="solar:move-bold-duotone" onClick={beginLayoutEdit} disabled={!sections.length}>Edit layout</Button>
            {hasFreeformSections && <Button variant={placementMode ? 'primary' : 'secondary'} size="sm" icon="solar:map-point-add-bold-duotone"
              onClick={() => { setPlacementMode(value => !value); setSelectedLot(null); }}>{placementMode ? 'Placing lot…' : 'Place lot'}</Button>}
          </div>}
        </div>
        {(viewMode === 'map' || viewMode === 'list') && !loading && !loadError && <>
          <div className="inventory-filters">
            <div className="inventory-search"><InputField label="Search inventory" placeholder="Lot, occupant, or section" icon="solar:magnifer-bold-duotone" value={searchQuery} onChange={event => { setSearchQuery(event.target.value); setSelectedLot(null); }} /></div>
            <div className="inventory-section-filter"><label htmlFor="inventory-section">Section</label><select id="inventory-section" value={filterSection} onChange={event => { setFilterSection(event.target.value); setSelectedLot(null); }}>
              <option value="">All active sections</option>{sections.filter(section => section.isActive).map(section => <option key={section.id} value={section.id}>{section.name} — {section.label}</option>)}
            </select></div>
            {(searchQuery || filterSection || filterStatus) && <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setFilterSection(''); setFilterStatus(''); setSelectedLot(null); }}>Clear filters</Button>}
            <span className="inventory-result-count" role="status">{filteredLots.length} of {stats.total} lots match</span>
          </div>
          {renderStatusBar()}
        </>}
        {loading ? <div className="inventory-state" role="status">Loading park inventory…</div>
          : loadError ? <div className="inventory-state" role="alert"><strong>Inventory unavailable</strong><p>{loadError}</p><Button variant="secondary" onClick={fetchInventory}>Try again</Button></div>
          : <>{viewMode === 'map' && renderMapView()}{viewMode === 'list' && renderListView()}{viewMode === 'designer' && renderDesigner()}{viewMode === 'policies' && renderPolicies()}</>}
        {renderDrawer()}
      </div>

      <Modal isOpen={showSectionModal} onClose={() => { if(!savingSetup)setShowSectionModal(false); }} title={editingSection ? `Edit ${editingSection.label}` : 'New section'} width="660px"
        footer={<><Button variant="secondary" disabled={savingSetup} onClick={()=>setShowSectionModal(false)}>Cancel</Button><Button variant="primary" loading={savingSetup} onClick={handleSaveSection}>{editingSection?'Save section':'Create section'}</Button></>}>
        <div className="inventory-setup-form">
          <div className="setup-fields"><InputField label="Section code" placeholder="e.g. F" value={sectionForm.name || ''} onChange={e=>setSectionForm({...sectionForm,name:e.target.value})}/><InputField label="Section name" placeholder="e.g. Garden of Hope" value={sectionForm.label || ''} onChange={e=>setSectionForm({...sectionForm,label:e.target.value})}/>
            <label>Category<select value={sectionForm.category||'LAWN'} onChange={e=>setSectionForm({...sectionForm,category:e.target.value,initialLotTypeId:lotTypes.find(type=>type.isActive&&type.category===e.target.value)?.id||''})}>{Object.entries(CATEGORY_CONFIG).map(([key,value])=><option key={key} value={key}>{value.label}</option>)}</select></label>
            <InputField label="Phase (optional)" value={sectionForm.phase||''} onChange={e=>setSectionForm({...sectionForm,phase:e.target.value})}/>
          </div>
          <InputField label="Description (optional)" value={sectionForm.description||''} onChange={e=>setSectionForm({...sectionForm,description:e.target.value})}/>
          <label>Layout<select value={sectionForm.layoutMode||'GRID'} onChange={e=>setSectionForm({...sectionForm,layoutMode:e.target.value})}><option value="GRID" disabled={!!editingSection && lots.some(lot=>lot.sectionId===editingSection.id&&lot.isFreeform)}>Grid · uniform rows and columns</option><option value="HYBRID">Hybrid · grid plus custom lots</option><option value="FREEFORM">Freeform · place and draw each lot</option></select></label>
          <p>{sectionForm.layoutMode==='FREEFORM'?'Place rectangular or polygon lots individually after saving this section.':editingSection?'Grid settings reposition existing grid lots. Changing row or column counts does not add or remove records.':'The section and its grid lots will be created together. Choose the lot type below.'}</p>
          {sectionForm.layoutMode!=='FREEFORM' && <>
            {!editingSection&&<label>Type for new grid lots<select value={sectionForm.initialLotTypeId||''} onChange={e=>{const type=lotTypes.find(t=>t.id===e.target.value);setSectionForm({...sectionForm,initialLotTypeId:e.target.value,lotWidth:type?.defaultWidth||48,lotHeight:type?.defaultHeight||48});}}><option value="">Choose a lot type</option>{lotTypes.filter(type=>type.isActive&&type.category===sectionForm.category).map(type=><option key={type.id} value={type.id}>{type.name} · {fmt(type.basePrice)}</option>)}</select></label>}
            <div className="setup-fields">{[['rows','Rows'],['cols','Columns'],['lotWidth','Lot width'],['lotHeight','Lot height'],['lotGap','Gap between lots']].map(([key,label])=><InputField key={key} label={label} type="number" value={String(sectionForm[key]??0)} onChange={e=>setSectionForm({...sectionForm,[key]:Number(e.target.value)})}/>)}</div>
            {!editingSection&&<p>{(sectionForm.rows||0)*(sectionForm.cols||0)} lots will be generated. Dimensions use map units, not physical land measurements.</p>}
          </>}
          <details><summary>Map position & section size</summary><div className="setup-fields">{[['x','X position'],['y','Y position'],...(sectionForm.layoutMode==='FREEFORM'?[['width','Section width'],['height','Section height']]:[])].map(([key,label])=><InputField key={key} label={label} type="number" value={String(sectionForm[key]??0)} onChange={e=>setSectionForm({...sectionForm,[key]:Number(e.target.value)})}/>)}</div><p>You can also move this section using Edit layout on the map.</p></details>
        </div>
      </Modal>
      <Modal isOpen={showLotTypeModal} onClose={()=>{if(!savingSetup)setShowLotTypeModal(false);}} title={editingLotType ? `Edit ${editingLotType.name}` : 'New lot type'} width="620px"
        footer={<><Button variant="secondary" disabled={savingSetup} onClick={()=>setShowLotTypeModal(false)}>Cancel</Button><Button variant="primary" loading={savingSetup} onClick={handleSaveLotType}>Save lot type</Button></>}>
        <div className="inventory-setup-form"><p>Set the defaults for new lots of this type. Existing lot prices and dimensions will not be overwritten.</p>
          <div className="setup-fields"><InputField label="Type name" value={lotTypeForm.name||''} onChange={e=>setLotTypeForm({...lotTypeForm,name:e.target.value})}/><InputField label="Type code" value={lotTypeForm.code||''} onChange={e=>setLotTypeForm({...lotTypeForm,code:e.target.value})}/><label>Category<select value={lotTypeForm.category||'LAWN'} onChange={e=>setLotTypeForm({...lotTypeForm,category:e.target.value})}>{Object.entries(CATEGORY_CONFIG).map(([key,value])=><option key={key} value={key}>{value.label}</option>)}</select></label><InputField label="Base price (₱)" type="number" value={String(lotTypeForm.basePrice??0)} onChange={e=>setLotTypeForm({...lotTypeForm,basePrice:Number(e.target.value)})}/></div>
          <InputField label="Description (optional)" value={lotTypeForm.description||''} onChange={e=>setLotTypeForm({...lotTypeForm,description:e.target.value})}/>
          <section><h3>Capacity & map dimensions</h3><div className="setup-fields">{[['capacity','Interment capacity'],['defaultWidth','Default width'],['defaultHeight','Default height']].map(([key,label])=><InputField key={key} label={label} type="number" value={String(lotTypeForm[key]??0)} onChange={e=>setLotTypeForm({...lotTypeForm,[key]:Number(e.target.value)})}/>)}</div></section>
          <p>Dimensions are map units. They set the initial size of a lot and do not represent surveyed land area.</p>
        </div>
      </Modal>
      <Modal isOpen={showFreeformModal} onClose={()=>{if(!savingFreeform)setShowFreeformModal(false);}} title={`Add lot · ${freeformSection?.label||''}`} width="700px"
        footer={<><Button variant="secondary" disabled={savingFreeform} onClick={()=>setShowFreeformModal(false)}>Cancel</Button><Button variant="primary" disabled={!shapeValid} loading={savingFreeform} onClick={handleSaveFreeformLot}>Create lot</Button></>}>
        <div className="inventory-setup-form"><div className="setup-fields">
          <InputField label="Lot number" value={freeformForm.lotNumber||''} onChange={e=>setFreeformForm({...freeformForm,lotNumber:e.target.value})}/>
          <label>Lot type<select value={freeformForm.lotTypeId||''} onChange={e=>{const type=lotTypes.find(t=>t.id===e.target.value);setFreeformForm({...freeformForm,lotTypeId:e.target.value,price:type?.basePrice??freeformForm.price,width:type?.defaultWidth??freeformForm.width,height:type?.defaultHeight??freeformForm.height});}}><option value="">No type</option>{lotTypes.filter(type=>type.isActive).map(type=><option key={type.id} value={type.id}>{type.name}</option>)}</select></label>
          <InputField label="Listed price (₱)" type="number" value={String(freeformForm.price??0)} onChange={e=>setFreeformForm({...freeformForm,price:Number(e.target.value)})}/>
        </div>
        {showFreeformModal&&<PolygonEditor key={freeformSection?.id} value={freeformForm.points??null} onChange={points=>setFreeformForm((current:any)=>({...current,points}))} onValidityChange={setShapeValid} width={Number(freeformForm.width)} height={Number(freeformForm.height)} disabled={savingFreeform}/>}
        <section><h3>Dimensions & placement</h3><div className="setup-fields">{[['width','Width'],['height','Height'],['x_coord','X offset from section'],['y_coord','Y offset from section']].map(([key,label])=><InputField key={key} label={label} type="number" value={String(freeformForm[key]??0)} onChange={e=>setFreeformForm({...freeformForm,[key]:Number(e.target.value)})}/>)}</div><p>Use map units. You can drag and resize the saved lot in Edit layout.</p></section>
        </div>
      </Modal>
    </>
  );
};
export default Inventory;
